import React, { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { env } from '../../config/env';
import '../../assets/css/theme.css';
import '../../assets/css/shop.css';   // shared page-header band (.ffs-hero*)
import '../../assets/css/pages.css';

type ContactField = 'name' | 'email' | 'phone' | 'message';
type ContactForm = Record<ContactField, string>;
type ContactErrors = Partial<Record<ContactField, string>>;
type ContactTouched = Partial<Record<ContactField, boolean>>;

const SUPPORT_HOURS = 'Mon – Sat, 10:00 am – 6:00 pm';

const ContactUs = () => {
  const [form, setForm] = useState<ContactForm>({ name: '', email: '', phone: '', message: '' });
  const [focused, setFocused] = useState<string>('');
  const [touched, setTouched] = useState<ContactTouched>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRe = /^[\d\s+\-().]{7,15}$/;

  const validate = (v: ContactForm): ContactErrors => {
    const e: ContactErrors = {};
    if (!v.name.trim()) e.name = 'Name is required';
    if (!v.email.trim()) e.email = 'Email is required';
    else if (!emailRe.test(v.email)) e.email = 'Enter a valid email';
    if (!v.phone.trim()) e.phone = 'Phone number is required';
    else if (!phoneRe.test(v.phone)) e.phone = 'Enter a valid phone number';
    if (!v.message.trim()) e.message = 'Message is required';
    return e;
  };

  const errors = validate(form);
  const isValid = Object.keys(errors).length === 0;

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleBlur = (field: ContactField) => {
    setFocused('');
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true, message: true });
    if (!isValid) return;

    // There is no contact endpoint on the backend, so hand the message to the visitor's
    // email app (pre-filled) instead of pretending it was sent. The form is kept
    // filled so nothing is lost if no email app is configured.
    const subject = `Website enquiry from ${form.name.trim()}`;
    const body = [
      form.message.trim(),
      '',
      '—',
      `Name: ${form.name.trim()}`,
      `Email: ${form.email.trim()}`,
      `Phone: ${form.phone.trim()}`,
    ].join('\n');
    window.location.href = `mailto:${env.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 6000);
  };

  const fieldErr = (f: ContactField) => touched[f] && errors[f];

  const infoItems = [
    {
      label: 'Manufactured & Marketed By',
      value: env.companyName,
      icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="8" width="14" height="9" rx="1" stroke="currentColor" strokeWidth="1"/><path d="M6 8V6a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>),
    },
    {
      label: 'Address',
      value: env.companyAddress,
      icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2a5 5 0 015 5c0 3.5-5 9-5 9S4 10.5 4 7a5 5 0 015-5z" stroke="currentColor" strokeWidth="1"/><circle cx="9" cy="7" r="1.5" stroke="currentColor" strokeWidth="1"/></svg>),
    },
    {
      label: 'Customer Support',
      value: env.supportPhoneDisplay,
      sub: SUPPORT_HOURS,
      icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 4.5A1.5 1.5 0 014.5 3h1.382a.5.5 0 01.447.276l1.5 3a.5.5 0 01-.112.563L6.5 8.062C7.53 10.07 9.93 12.47 11.937 13.5l1.223-1.217a.5.5 0 01.563-.112l3 1.5A.5.5 0 0117 14.118V15.5A1.5 1.5 0 0115.5 17C8.044 17 1 9.956 1 2.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>),
    },
    {
      label: 'Email',
      value: env.supportEmail,
      icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="4" width="16" height="11" rx="1.5" stroke="currentColor" strokeWidth="1"/><path d="M1 5.5l8 5 8-5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>),
    },
  ];

  const errIcon = (
    <svg width="12" height="12" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <circle cx="5" cy="5" r="4.5" stroke="currentColor" strokeWidth="1" />
      <path d="M5 3v2.5M5 7v.3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );

  return (
    <main className="ffp-page">
      {/* ── Header band ── */}
      <header className="ffs-hero ffs-hero--tall">
        <div className="ffh-container ffs-hero-inner">
          <div>
            <nav className="ffs-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span className="is-current">Contact</span>
            </nav>
            <h1 className="ffs-hero-title">Contact <em>us</em></h1>
          </div>
          <p className="ffs-hero-sub">We'd love to hear from you — we usually reply within 48 hours.</p>
        </div>
      </header>

      <div className="ffh-container">
        {/* ── Info cards ── */}
        <div className="ffp-info-grid">
          {infoItems.map((item, i) => (
            <div className="ffp-info-card" key={i}>
              <span className="ffp-info-icon">{item.icon}</span>
              <div>
                <div className="ffp-info-label">{item.label}</div>
                <div className="ffp-info-value">{item.value}</div>
                {item.sub && <div className="ffp-info-sub">{item.sub}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* ── Panel + form ── */}
        <div className="ffp-contact">
          <aside className="ffp-panel">
            <p className="ffh-eyebrow ffh-eyebrow--light ffh-eyebrow--left">Get in touch</p>
            <h2 className="ffp-panel-title">We're here <em>for you</em></h2>
            <p className="ffp-panel-text">
              Questions about an order, a style or a fabric? Write to us, call, or visit the studio.
            </p>

            <ul className="ffp-panel-list">
              <li>
                <span>Phone</span>
                <a href={`tel:${env.supportPhone}`}>{env.supportPhoneDisplay}</a>
                <small>{SUPPORT_HOURS}</small>
              </li>
              <li>
                <span>Email</span>
                <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a>
              </li>
              <li>
                <span>Studio</span>
                <em>{env.companyAddress}</em>
              </li>
            </ul>

            <div className="ffp-panel-arch" aria-hidden="true"><span /><span /></div>
          </aside>

          <section className="ffp-form-card" aria-labelledby="ffp-form-title">
            <h2 id="ffp-form-title" className="ffp-form-title">Send a <em>message</em></h2>
            <p className="ffp-form-sub">All fields are required. Your email app will open with the message ready to send.</p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="ffp-form-row">
                <div className={`ffp-field${fieldErr('name') ? ' has-error' : ''}`}>
                  <label htmlFor="cu-name">Name</label>
                  <input id="cu-name" type="text" name="name" value={form.name} placeholder="Your full name"
                    autoComplete="name" onChange={handleChange} onFocus={() => setFocused('name')} onBlur={() => handleBlur('name')} />
                  {fieldErr('name') && <span className="ffp-field-err">{errIcon}{errors.name}</span>}
                </div>
                <div className={`ffp-field${fieldErr('email') ? ' has-error' : ''}`}>
                  <label htmlFor="cu-email">Email</label>
                  <input id="cu-email" type="email" name="email" value={form.email} placeholder="you@example.com"
                    autoComplete="email" onChange={handleChange} onFocus={() => setFocused('email')} onBlur={() => handleBlur('email')} />
                  {fieldErr('email') && <span className="ffp-field-err">{errIcon}{errors.email}</span>}
                </div>
              </div>

              <div className={`ffp-field${fieldErr('phone') ? ' has-error' : ''}`}>
                <label htmlFor="cu-phone">Phone number</label>
                <input id="cu-phone" type="text" inputMode="tel" name="phone" value={form.phone} placeholder="+91 00000 00000"
                  autoComplete="tel" onChange={handleChange} onFocus={() => setFocused('phone')} onBlur={() => handleBlur('phone')} />
                {fieldErr('phone') && <span className="ffp-field-err">{errIcon}{errors.phone}</span>}
              </div>

              <div className={`ffp-field${fieldErr('message') ? ' has-error' : ''}`}>
                <label htmlFor="cu-message">Message</label>
                <textarea id="cu-message" name="message" rows={5} value={form.message} placeholder="Tell us how we can help…"
                  onChange={handleChange} onFocus={() => setFocused('message')} onBlur={() => handleBlur('message')} />
                {fieldErr('message') && <span className="ffp-field-err">{errIcon}{errors.message}</span>}
              </div>

              <button type="submit" className="ffh-btn ffp-submit">Send message</button>
              <p className="ffp-form-note">
                No email app? Write to <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a> directly.
              </p>
            </form>
          </section>
        </div>
      </div>

      <div className={`ffp-toast${submitted ? ' is-show' : ''}`} role="status" aria-live="polite">
        Opening your email app — press send there to reach us.
      </div>
    </main>
  );
};

export default ContactUs;